import React from "react";
import { AnalyticsApi } from "../../api/user";
import { Details, Height, HeightUnits } from "../../models/Details";
import { Util } from "../../models/Util";

interface HeightProp {
	detail: Details;
	valueSelected(response?: any): void;
}

const HeightView: React.FC<HeightProp> = ({ detail, valueSelected }): JSX.Element => {
	const [heightUnit, setHeightUnit] = React.useState(HeightUnits.feet);
	const [height, setHeight] = React.useState({ heightCm: 0, heightFt: 0, heightInch: 0 });
	const [enabled, setEnabled] = React.useState(false);

	const valueClicked = () => {
		AnalyticsApi.logEvent('Height Added');
		detail.height!.selectedUnit = heightUnit;
		detail.setHeight(detail.height!);
		valueSelected(height);
	};

	const selectUnit = (unit: HeightUnits) => {
		setHeightUnit(unit);
	};

	const changeHeightCm = (event: any) => {
		let newHeight = {
			heightCm: parseInt(event.target.value),
			heightFt: height.heightFt,
			heightInch: 0,
			selectedUnit: heightUnit,
		};
		const heightConverted = Util.convertCmToFt(event.target.value);
		newHeight.heightFt = heightConverted.feet;
		newHeight.heightInch = heightConverted.inches;

		checkValidity(newHeight);
	};

	const changeHeightFt = (event: any) => {
		let newHeight = {
			heightCm: height.heightCm,
			heightFt: parseInt(event.target.value),
			heightInch: height.heightInch,
			selectedUnit: heightUnit,
		};
		const heightConverted = Util.convertFtToCm(newHeight.heightFt, newHeight.heightInch);
		newHeight.heightCm = heightConverted;

		checkValidity(newHeight);
	};

	const changeHeightInch = (event: any) => {
		let newHeight = {
			heightCm: height.heightCm,
			heightFt: height.heightFt,
			heightInch: parseInt(event.target.value),
			selectedUnit: heightUnit,
		};
		const heightConverted = Util.convertFtToCm(newHeight.heightFt, newHeight.heightInch);
		newHeight.heightCm = heightConverted;

		checkValidity(newHeight);
	};

	const checkValidity = (newHeight: any) => {
		setHeight(newHeight);
		if (detail.setHeight(newHeight)) {
			setEnabled(true);
		} else {
			setEnabled(false);
		}
	};

	React.useEffect(() => {
		if (typeof detail.height !== "undefined") {
			checkValidity(detail.height);
			setHeightUnit(detail.height.selectedUnit!);
		}
	}, []);

	const renderChooser = () => {
		return (
			<div className="options">
				<div
					className={(heightUnit === HeightUnits.feet ? "active" : "") + " option"}
					onClick={(e) => {
						selectUnit(HeightUnits.feet);
					}}
				>
					<div className='text'>ft</div>
				</div>
				<div
					className={(heightUnit === HeightUnits.cm ? "active" : "") + " option"}
					onClick={(e) => {
						selectUnit(HeightUnits.cm);
					}}
				>
					<div className='text'>cm</div>
				</div>
			</div>
		);
	};

	const renderInputFields = () => {
		if (heightUnit === HeightUnits.feet) {
			return (
				<>
					<div className="inputDiv">
						<div className="small-input-inner">
							<input
								type="number"
								min="2"
								max="9"
								className="normalInput smallNumber"
								value={height.heightFt === 0 ? "" : height.heightFt.toString()}
								onChange={changeHeightFt}
							/>
							<span>ft</span>
						</div>
					</div>

					<div className="inputDiv">
						<div className="small-input-inner">
							<input
								type="number"
								min="0"
								max="13"
								className="normalInput smallNumber"
								value={height.heightInch === 0 && height.heightFt === 0 ? "" : height.heightInch.toString()}
								onChange={changeHeightInch}
							/>
							<span>in</span>
						</div>
					</div>

					<div className="smallSubtext">Valid height is 2'-9'11"</div>
				</>
			);
		} else {
			return (
				<>
					<div className="inputDiv">
						<div className="large-input-inner">
							<input
								type="number"
								min="60"
								max="300"
								className="normalInput largeNumberOne"
								value={height.heightCm === 0 ? "" : height.heightCm.toString()}
								onChange={changeHeightCm}
							/>
							<span>cm</span>
						</div>
					</div>

					<div className="smallSubtext">Valid height is 60-300cm</div>
				</>
			);
		}
	};

	return (
		<div className="content">
			{renderChooser()}

			{renderInputFields()}
			<div className="">
				{enabled ? (
					<button className="greenButton" onClick={(e) => valueClicked()}>
						Next
					</button>
				) : (
					<button className="lightGrayButton">Next</button>
				)}
			</div>
		</div>
	);
};

export default HeightView;
